import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, RefreshCw, CheckCircle2, Circle, Phone, Calendar, MapPin, LogOut } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminBookings() {
  const [code, setCode] = useState(sessionStorage.getItem('nk_admin_code') || '');
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchBookings = useCallback(async (adminCode) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/bookings`, { headers: { 'X-Admin-Code': adminCode } });
      setBookings(res.data);
      sessionStorage.setItem('nk_admin_code', adminCode);
      setCode(adminCode);
    } catch (e) {
      if (e.response?.status === 401) {
        setError('Galat access code. Try again.');
        sessionStorage.removeItem('nk_admin_code');
        setCode('');
      } else {
        setError('Could not load bookings. Please retry.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (code) fetchBookings(code);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStatus = async (b) => {
    const status = b.status === 'contacted' ? 'new' : 'contacted';
    await axios.patch(`${API}/api/bookings/${b.id}`, { status }, { headers: { 'X-Admin-Code': code } });
    setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, status } : x)));
  };

  const logout = () => {
    sessionStorage.removeItem('nk_admin_code');
    setCode('');
    setBookings([]);
  };

  const inputCls =
    'w-full bg-[#fffaf0] border border-[#e2c887] rounded-lg px-4 py-3 font-body text-lg text-[#4a2f16] placeholder-[#b79a6a] focus:outline-none focus:border-[#c9962e] focus:ring-2 focus:ring-[#c9962e]/30 transition';

  if (!code) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5" style={{ background: 'linear-gradient(180deg, #fdf6e3, #f6e9cd)' }}>
        <div
          className="w-full max-w-sm rounded-3xl p-8 text-center"
          style={{ background: 'linear-gradient(180deg, #fffaf0, #f6e9cd)', border: '1.5px solid #d9a63b', boxShadow: '0 20px 50px rgba(124,19,15,0.12)' }}
        >
          <div
            className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
            style={{ background: 'radial-gradient(circle at 40% 30%, #a71c14, #5a0d0a)', border: '2px solid #d9a63b' }}
          >
            <Lock className="text-[#f2d68a]" size={26} />
          </div>
          <h1 className="font-display font-bold text-3xl text-[#7c130f]">Admin Access</h1>
          <p className="font-body text-[#5a3a1c] mt-2 mb-5">Enter your access code to view bookings</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (input.trim()) fetchBookings(input.trim());
            }}
          >
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Access code"
              data-testid="admin-code-input"
              className={inputCls}
            />
            {error && <p data-testid="admin-error" className="text-[#a71c14] text-sm mt-2 font-body">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full mt-4 py-3 rounded-full font-heading tracking-[0.15em] text-[#fff8e7] transition-all duration-300 hover:scale-[1.02] disabled:opacity-60"
              style={{ background: 'linear-gradient(90deg, #a71c14, #7c130f, #a71c14)', border: '1.5px solid #d9a63b' }}
            >
              {loading ? 'CHECKING...' : 'ENTER'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-5 py-10 md:px-10" style={{ background: 'linear-gradient(180deg, #fdf6e3, #f6e9cd)' }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display font-bold text-4xl text-[#7c130f]">Booking Enquiries</h1>
            <p className="font-body text-[#5a3a1c] mt-1">
              {bookings.length} total &middot; {bookings.filter((b) => b.status === 'new').length} new
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => fetchBookings(code)}
              data-testid="admin-refresh-btn"
              className="px-5 py-2.5 rounded-full font-heading text-sm tracking-wide text-[#7c130f] bg-[#f4e6c8] hover:bg-[#ecd9a8] transition flex items-center gap-2"
              style={{ border: '1.5px solid #c9962e' }}
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="px-5 py-2.5 rounded-full font-heading text-sm tracking-wide text-[#fff8e7] transition flex items-center gap-2"
              style={{ background: 'linear-gradient(90deg, #a71c14, #7c130f)', border: '1.5px solid #d9a63b' }}
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>

        {error && <p className="text-[#a71c14] font-body mb-4">{error}</p>}

        {bookings.length === 0 && !loading ? (
          <div
            className="rounded-2xl p-12 text-center font-body text-lg text-[#5a3a1c]"
            style={{ background: '#fffaf0', border: '1.5px solid #e2c887' }}
            data-testid="admin-empty-state"
          >
            No booking enquiries yet.
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-bookings-list">
            {bookings.map((b) => (
              <div
                key={b.id}
                data-testid={`booking-card-${b.id}`}
                className="rounded-2xl p-5 md:p-6"
                style={{
                  background: b.status === 'contacted' ? '#f8f2e2' : '#fffaf0',
                  border: `1.5px solid ${b.status === 'contacted' ? '#d8c79a' : '#d9a63b'}`,
                  boxShadow: '0 8px 24px rgba(124,19,15,0.07)',
                  opacity: b.status === 'contacted' ? 0.75 : 1,
                }}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading font-semibold text-xl text-[#7c130f]">{b.name}</h3>
                      <span
                        className="px-3 py-0.5 rounded-full font-heading text-xs tracking-wider text-[#f2d68a]"
                        style={{ background: b.status === 'contacted' ? '#8a6535' : 'linear-gradient(90deg, #a71c14, #7c130f)' }}
                      >
                        {b.status === 'contacted' ? 'CONTACTED' : 'NEW'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 font-body text-[#5a3a1c]">
                      <a href={`tel:${b.phone}`} className="flex items-center gap-1.5 hover:text-[#a71c14]">
                        <Phone size={14} /> {b.phone}
                      </a>
                      <span className="flex items-center gap-1.5">
                        <Calendar size={14} /> {b.date}
                      </span>
                      {b.city && (
                        <span className="flex items-center gap-1.5">
                          <MapPin size={14} /> {b.city}
                        </span>
                      )}
                    </div>
                    <p className="font-heading text-sm tracking-wide text-[#c9962e] mt-2 uppercase">{b.service}</p>
                    {b.message && <p className="font-body italic text-[#7c4a20] mt-1">"{b.message}"</p>}
                    <p className="font-body text-xs text-[#b79a6a] mt-2">
                      Received: {new Date(b.created_at).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleStatus(b)}
                    data-testid={`toggle-status-${b.id}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-full font-heading text-sm tracking-wide text-[#7c130f] bg-[#f4e6c8] hover:bg-[#ecd9a8] transition"
                    style={{ border: '1.5px solid #c9962e' }}
                  >
                    {b.status === 'contacted' ? <Circle size={15} /> : <CheckCircle2 size={15} />}
                    {b.status === 'contacted' ? 'Mark New' : 'Mark Contacted'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
